"use client";
import { EmblaCarouselType, EmblaOptionsType } from "embla-carousel";
import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from "embla-carousel-react";
import {
  createContext,
  forwardRef,
  ReactNode,
  Ref,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { ArrowButton } from "@/components/molecules/ListingCardButtons";
import { cn } from "@/lib/utils";

type CarouselApi = UseEmblaCarouselType[1];

interface CarouselContextType {
  carouselRef: Ref<HTMLDivElement>;
  api: EmblaCarouselType | null;
  opts: EmblaOptionsType;
  orientation: string;
  scrollPrev: () => void;
  scrollNext: () => void;
  scrollTo: (index: number) => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
  activeIndex: number;
}

const CarouselContext = createContext<CarouselContextType | null>(null);

function useCarousel() {
  const context = useContext(CarouselContext);

  if (!context) {
    throw new Error("useCarousel must be used within a <Carousel />");
  }

  return context;
}

type DotNavigationProps = {
  className?: string;
  totalItems: number;
};

const DotNavigation = ({ className, totalItems }: DotNavigationProps) => {
  const { scrollTo, activeIndex } = useCarousel();
  const maxItems = Math.min(totalItems, 5);
  const goToSlide = (index: number) => {
    scrollTo(index);
  };

  const getDotClass = (index: number) => {
    let activeDotClasses =
      "tw-w-6 tw-h-6 tw-bg-base-white tw-rounded-circular tw-mx-2";
    let inactiveDotClasses =
      "tw-w-6 tw-h-6 tw-bg-base-white tw-rounded-circular tw-mx-2 tw-opacity-60";
    let endcapDotClasses =
      "tw-w-4 tw-h-4 tw-bg-base-white tw-rounded-circular tw-mx-2 tw-opacity-60";

    if (totalItems < 5) {
      return index === activeIndex ? activeDotClasses : inactiveDotClasses;
    }

    if (activeIndex >= 2 && activeIndex < totalItems - 2) {
      if (index === 2) return activeDotClasses;
      if (index === 0 || index === 4) return endcapDotClasses;
      return;
    }

    if (activeIndex < 2) {
      if (index === activeIndex) return activeDotClasses;
      if (index === 4) return endcapDotClasses;
      return;
    }

    if (activeIndex === totalItems - 2) {
      if (index === 3) return activeDotClasses;
      if (index === 0) return endcapDotClasses;
      return;
    }

    if (activeIndex === totalItems - 1) {
      if (index === 4) return activeDotClasses;
      if (index === 0) return endcapDotClasses;
      return;
    }

    if (index === 0 || index === 4) {
      if (index === activeIndex) return activeDotClasses;
      return endcapDotClasses;
    }

    if (index === activeIndex) return activeDotClasses;
  };

  return (
    <div
      role="tablist"
      aria-label="Carousel Navigation"
      className={cn(
        className,
        "tw-flex tw-mb-12 tw-justify-center tw-relative tw-items-center",
      )}
    >
      {Array.from({ length: maxItems }).map((_, index) => {
        const isActive = index === activeIndex;
        const slideId = `carousel-slide-${index}`;

        return (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            role="tab"
            id={`carousel-tab-${index}`}
            aria-selected={isActive}
            aria-controls={slideId}
            aria-label={`Slide ${index + 1} of ${maxItems}`}
            tabIndex={isActive ? 0 : -1}
            className={cn(
              getDotClass(index) ||
                "tw-w-6 tw-h-6 tw-bg-base-white tw-rounded-circular tw-mx-2 tw-opacity-60",
              "tw-border-0 tw-cursor-pointer",
            )}
            type="button"
          />
        );
      })}
    </div>
  );
};
DotNavigation.displayName = "DotNavigation";

interface CarouselContentProps {
  className?: string;
  children: ReactNode;
}

const CarouselContent = forwardRef<HTMLDivElement, CarouselContentProps>(
  ({ className, children, ...props }, ref) => {
    const { carouselRef, orientation } = useCarousel();

    return (
      <div ref={carouselRef} className="tw-h-full">
        <div
          ref={ref}
          className={cn(
            "tw-flex",
            orientation === "horizontal" ? "-tw-ml-4" : "-tw-mt-4 tw-flex-col",
            className,
          )}
          {...props}
        >
          {children}
        </div>
      </div>
    );
  },
);
CarouselContent.displayName = "CarouselContent";

interface CarouselItemProps {
  className?: string;
  children: ReactNode;
  index?: number;
}

const CarouselItem = forwardRef<HTMLDivElement, CarouselItemProps>(
  ({ className, children, index = 0, ...props }, ref) => {
    const { activeIndex, orientation } = useCarousel();

    return (
      <div
        ref={ref}
        id={`carousel-slide-${index}`}
        role="tabpanel"
        aria-labelledby={`carousel-tab-${index}`}
        tabIndex={activeIndex !== index ? -1 : 0}
        className={cn(
          "tw-min-w-0 tw-shrink-0 tw-grow-0 tw-basis-full",
          orientation === "horizontal" ? "tw-pl-4" : "tw-pt-4",
          className,
        )}
        {...props}
      >
        {children}
      </div>
    );
  },
);
CarouselItem.displayName = "CarouselItem";

interface CarouselButtonProps {
  className?: string;
  variant?: string;
  size?: string;
}

const CarouselPrevious = forwardRef<HTMLButtonElement, CarouselButtonProps>(
  ({ className }, ref) => {
    const { orientation, scrollPrev, canScrollPrev } = useCarousel();
    if (canScrollPrev) {
      return (
        <ArrowButton
          ref={ref}
          className={cn(
            "tw-absolute tw-z-[5] tw-pointer-events-auto",
            orientation === "horizontal"
              ? "tw-left-12 tw-top-1/2 tw--translate-y-1/2"
              : "tw-top-12 tw-left-1/2 tw--translate-x-1/2 tw-rotate-90",
            className,
          )}
          onClick={scrollPrev}
        />
      );
    }
    return null;
  },
);
CarouselPrevious.displayName = "CarouselPrevious";

const CarouselNext = forwardRef<HTMLButtonElement, CarouselButtonProps>(
  ({ className }, ref) => {
    const { orientation, scrollNext, canScrollNext } = useCarousel();
    if (canScrollNext) {
      return (
        <ArrowButton
          ref={ref}
          className={cn(
            "tw-absolute tw-z-[5] tw-pointer-events-auto",
            orientation === "horizontal"
              ? "tw-right-12 tw-top-1/2 tw--translate-y-1/2"
              : "tw-bottom-12 tw-left-1/2 tw--translate-x-1/2 tw-rotate-90",
            className,
          )}
          disabled={!canScrollNext}
          onClick={scrollNext}
          right
        />
      );
    }
    return null;
  },
);
CarouselNext.displayName = "CarouselNext";

interface CarouselProps {
  orientation?: string;
  opts?: EmblaOptionsType;
  setApi?: (api: EmblaCarouselType) => void;
  plugins?: any[];
  className?: string;
  children: ReactNode;
  totalItems?: number;
  disableSwipe?: boolean;
  startIndex?: number;
}

const Carousel = forwardRef<HTMLDivElement, CarouselProps>(
  (
    {
      orientation = "horizontal",
      opts = {},
      setApi,
      plugins,
      className,
      children,
      totalItems,
      startIndex,
      disableSwipe = false,
      ...props
    },
    ref,
  ) => {
    const [carouselRef, api] = useEmblaCarousel(
      {
        axis: orientation === "horizontal" ? "x" : "y",
        startIndex: startIndex || 0,
        watchDrag: !disableSwipe,
        ...opts,
      },
      plugins,
    );
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);

    const onSelect = useCallback((api: EmblaCarouselType) => {
      if (!api) {
        return;
      }

      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
      setActiveIndex(api.selectedScrollSnap());
    }, []);

    const scrollPrev = useCallback(() => {
      setActiveIndex((prevIndex) => Math.max(prevIndex - 1, 0));
      api?.scrollPrev();
    }, [api]);

    const scrollTo = useCallback(
      (index: number) => {
        if (totalItems) {
          setActiveIndex((prevIndex) =>
            Math.min(prevIndex + 1, totalItems - 1),
          ); // Ensure index doesn't exceed totalItems
        }
        api?.scrollTo(index);
      },
      [api],
    );

    const scrollNext = useCallback(() => {
      setActiveIndex(activeIndex + 1);
      api?.scrollNext();
    }, [api]);

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          scrollPrev();
        } else if (event.key === "ArrowRight") {
          event.preventDefault();
          scrollNext();
        }
      },
      [scrollPrev, scrollNext],
    );

    useEffect(() => {
      if (!api || !setApi) {
        return;
      }

      setApi(api);
    }, [api, setApi]);

    useEffect(() => {
      if (!api) {
        return;
      }

      onSelect(api);
      api.on("reInit", onSelect);
      api.on("select", onSelect);

      return () => {
        api?.off("select", onSelect);
      };
    }, [api, onSelect]);

    return (
      <CarouselContext.Provider
        value={{
          carouselRef,
          api: api ?? null,
          opts,
          orientation:
            orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
          scrollPrev,
          scrollNext,
          scrollTo,
          canScrollPrev,
          canScrollNext,
          activeIndex,
        }}
      >
        <div
          ref={ref}
          onKeyDownCapture={handleKeyDown}
          className={cn("tw-relative tw-w-full tw-overflow-hidden tw-touch-pan-y", className)}
          role="region"
          aria-roledescription="carousel"
          data-testid="carousel-component"
          {...props}
        >
          {children}
        </div>
      </CarouselContext.Provider>
    );
  },
);
Carousel.displayName = "Carousel";

export {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  DotNavigation,
  type CarouselApi,
};
